/**
 * Design v2 navigation (phase 2). Types for the `navigation` shared prop the server sends to
 * flag-on users (Navigation::Builder), plus the helpers every v2 shell surface shares: icon
 * mapping, active-state matching and tab resolution. The registry (DomainMenuCatalog::V2) is the
 * single source of destinations — nothing here hard-codes a menu.
 */
import type { IconName } from '@/components/design/icons'

export type NavEntry = {
  id: string
  label: string
  href?: string
  icon?: string
  keywords?: string[]
  match?: string[]
  section?: string
  discover?: boolean
  default?: boolean
  pinned?: boolean
  external?: boolean
  children?: NavEntry[]
}

export type NavTab = {
  id: 'home' | 'shop' | 'muchi' | 'messages' | 'me'
  label: string
  href: string
  icon: string
  sections: NavEntry[]
}

export type NavWorkspace = {
  id: 'personal' | 'my_shop' | 'business' | 'admin'
  label: string
  icon: string
  home: string
  /** null = the count failed to load: hide the badge, never show 0 (IA appendix 3). */
  action_count: number | null
  items: NavEntry[]
}

export type ArcadeSummary = {
  player_id: string
  username: string
  level: number
  xp: number
  level_xp: number
  next_level_xp: number
  progress: number
  stamps: { id: string; name: string; unlocked_at: string }[]
  last_game: { id: string; name: string; launch_href: string; played_at: string } | null
  hub_href: string
}

export type NavigationProp = {
  tabs: NavTab[]
  top: NavEntry[]
  rail: NavEntry[]
  all_features_href: string
  workspaces: NavWorkspace[]
  current_workspace: NavWorkspace['id'] | null
  me_badge: { count: number; workspace: NavWorkspace['id'] | null } | null
  arcade_player: boolean
  arcade?: ArcadeSummary | null
  earn: NavEntry[]
}

export type AzEntry = NavEntry & { letter: string; location?: string }

/** Registry icon names (lucide-style strings) → the design Icon layer. */
const REGISTRY_ICONS: Record<string, IconName> = {
  activity: 'activity',
  'app-window': 'app',
  'badge-check': 'badgeCheck',
  'bar-chart': 'chart',
  bell: 'bell',
  bot: 'muchi',
  briefcase: 'briefcase',
  calendar: 'calendar',
  car: 'car',
  clapperboard: 'clapperboard',
  'clipboard-list': 'clipboard',
  coins: 'coins',
  'columns-3': 'columns',
  compass: 'compass',
  'file-text': 'file',
  film: 'film',
  flag: 'flag',
  'flask-conical': 'flask',
  gamepad: 'game',
  gauge: 'gauge',
  globe: 'globe',
  heart: 'heart',
  home: 'home',
  'layout-dashboard': 'dashboard',
  'life-buoy': 'help',
  list: 'list',
  mail: 'mail',
  map: 'map',
  'map-pin': 'mapPin',
  megaphone: 'megaphone',
  'messages-square': 'messages',
  navigation: 'navigation',
  newspaper: 'news',
  package: 'package',
  palette: 'palette',
  'plus-circle': 'plusCircle',
  route: 'route',
  search: 'search',
  settings: 'settings',
  'shield-alert': 'shieldAlert',
  'shield-check': 'shield',
  'shopping-bag': 'bag',
  'shopping-cart': 'cart',
  sparkles: 'sparkle',
  store: 'shop',
  ticket: 'ticket',
  'trending-up': 'trendingUp',
  trophy: 'trophy',
  truck: 'truck',
  user: 'user',
  users: 'users',
  video: 'video',
  wallet: 'wallet',
  warehouse: 'warehouse',
  wrench: 'wrench',
}

export function navIcon(name?: string): IconName {
  return (name && REGISTRY_ICONS[name]) || 'grid'
}

function pathOf(url: string): string {
  const clean = url.split(/[?#]/)[0] || '/'
  return clean.length > 1 ? clean.replace(/\/$/, '') : clean
}

/** Same rule as v1 isNavActive, plus the registry's extra `match` prefixes. */
export function pathMatches(href: string | undefined, currentUrl: string): boolean {
  if (!href) return false
  const path = pathOf(currentUrl)
  const target = pathOf(href)
  if (target === '/') return path === '/'
  return path === target || path.startsWith(`${target}/`)
}

export function entryActive(entry: NavEntry, currentUrl: string): boolean {
  if (pathMatches(entry.href, currentUrl)) return true
  if (entry.match?.some((m) => pathMatches(m, currentUrl))) return true
  return Boolean(entry.children?.some((c) => entryActive(c, currentUrl)))
}

/** Every href that belongs to a workspace (first-level items and their children). */
function workspaceOwns(ws: NavWorkspace, currentUrl: string): boolean {
  return ws.items.some((item) => entryActive(item, currentUrl))
}

/**
 * Which tab is lit for a URL. Tab sections light their tab (Shop › Services); the top-bar Cart
 * lights Shop; workspace pages and Me-side pages light Me. Anything else (LINK pages) lights none.
 */
export function activeTabId(nav: NavigationProp, currentUrl: string): NavTab['id'] | null {
  const path = pathOf(currentUrl)
  if (path === '/me' || path.startsWith('/me/') || path === '/features') return 'me'
  for (const tab of nav.tabs) {
    if (tab.id === 'home' && (path === '/' || pathMatches('/feed', path))) return 'home'
    if (tab.id !== 'home' && pathMatches(tab.href, path)) return tab.id
  }
  for (const tab of nav.tabs) {
    if (tab.sections.some((s) => entryActive(s, path))) return tab.id
  }
  if (nav.top.some((t) => t.id === 'cart' && pathMatches(t.href, path))) return 'shop'
  if (nav.workspaces.some((ws) => workspaceOwns(ws, path))) return 'me'
  return null
}

/** The workspace whose short nav should show for this URL (pill row under the top bar). */
export function workspaceForUrl(nav: NavigationProp, currentUrl: string): NavWorkspace | null {
  const path = pathOf(currentUrl)
  const current = nav.workspaces.find((w) => w.id === nav.current_workspace) ?? null
  // The Me hub lists the Personal items itself, so no pill row there.
  if (path === '/me') return null
  if (current && workspaceOwns(current, path)) return current
  return nav.workspaces.find((ws) => workspaceOwns(ws, path)) ?? null
}

export function formatCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}
